import moment from 'moment';
import type { Control } from './types';

// The rater validates date leaves against JSON Schema `format: date` (YYYY-MM-DD).
// Mantine/seeded values can carry a time component, so normalise on the boundary.
function toApiDate(raw: unknown): string | null {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  const parsed = moment.utc(raw as moment.MomentInput);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

// The value-wrapper convention (FPX-6075): a scalar field's value is { value: x };
// an array field's value is { values: [ { props: { <col>: { value } } } ] }.
type ScalarValue = { value: unknown };
type ArrayValue = { values: Array<RowValue | ScalarValue> };
// A group field's value is a single { props: { ... } } envelope (RowValue).
type CoverageValueProps = Record<string, ScalarValue | ArrayValue | RowValue>;
type RowValue = { props: CoverageValueProps };

export type FormValues = Record<string, unknown>;

function defaultFor(control: Control): unknown {
  switch (control.kind) {
    case 'toggle':
      return false;
    case 'array':
      return [];
    case 'date':
      // An empty date is null, not '': DatePickerInput renders '' as "Invalid Date".
      return null;
    default:
      return '';
  }
}

export function seedFormValues(
  controls: Control[],
  valueProps: CoverageValueProps | undefined
): FormValues {
  const seeded: FormValues = {};

  for (const control of controls) {
    const stored = valueProps?.[control.fieldKey];

    if (control.kind === 'array') {
      const rows = (stored as ArrayValue | undefined)?.values ?? [];
      seeded[control.fieldKey] = control.scalarItem
        ? rows.map((row) => ({ value: (row as ScalarValue).value }))
        : rows.map((row) =>
            seedFormValues(control.itemFields, (row as RowValue).props)
          );
      continue;
    }

    if (control.kind === 'group') {
      seeded[control.fieldKey] = seedFormValues(
        control.fields,
        (stored as RowValue | undefined)?.props
      );
      continue;
    }

    if (stored === undefined) {
      seeded[control.fieldKey] = defaultFor(control);
      continue;
    }

    const storedValue = (stored as ScalarValue).value;
    seeded[control.fieldKey] =
      control.kind === 'date' ? toApiDate(storedValue) : storedValue;
  }

  return seeded;
}

export function toCoverageProps(
  controls: Control[],
  formValues: FormValues
): { props: CoverageValueProps } {
  const props: CoverageValueProps = {};

  for (const control of controls) {
    const raw = formValues[control.fieldKey];

    if (control.kind === 'array') {
      const rows = Array.isArray(raw) ? (raw as FormValues[]) : [];
      props[control.fieldKey] = {
        values: control.scalarItem
          ? rows.map((row) => ({ value: row.value }))
          : rows.map((row) => toCoverageProps(control.itemFields, row)),
      };
      continue;
    }

    if (control.kind === 'group') {
      props[control.fieldKey] = toCoverageProps(
        control.fields,
        (raw as FormValues) ?? {}
      );
      continue;
    }

    props[control.fieldKey] = {
      value: control.kind === 'date' ? toApiDate(raw) : raw,
    };
  }

  return { props };
}
